import { WEBSITE_ANALYSIS_PERFORMANCE } from "app/pages/sales-intelligence/constants/routes";
import { ROUTES } from "single-spa/react-app/config/solutions25/genAIIntelligence/AITraffic/AITrafficConstants";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import type { IRedirectStateLiteToPro } from "single-spa/react-app/router/types";
import { EParamsName, EStateNameFromLite } from "single-spa/react-app/router/types";

export const redirectLiteToPro = (isSI: boolean): IRedirectStateLiteToPro | null => {
    const urlParams = new URLSearchParams(window.location.search);
    const actionParam = urlParams?.get(EParamsName.ACTION);
    const domainParam = urlParams?.get(EParamsName.DOMAIN);
    const keywordParam = urlParams?.get(EParamsName.KEYWORD);
    const industryParam = urlParams?.get(EParamsName.INDUSTRY);
    const appNameParam = urlParams?.get(EParamsName.APP_NAME);
    const searchSourceParam = urlParams?.get(EParamsName.SEARCH_SOURCE);
    const campaignIdParam = urlParams?.get(EParamsName.CAMPAIGN_ID);

    const urlParamsCopy = new URLSearchParams(urlParams);

    const paramNamesToDelete = Object.values(EParamsName);

    paramNamesToDelete.forEach((paramName) => urlParams.delete(paramName));

    let newUrl = `${location.pathname}`;
    if (urlParams && urlParams.toString()?.length > 0) {
        newUrl = `${newUrl}?${urlParams}`;
    }
    window.history.replaceState({}, "", newUrl);

    const defaultParams = { isWWW: "*", country: 999, duration: "3m", webSource: "Total" };
    const goToWebsiteState = (state: string): IRedirectStateLiteToPro => {
        const defaultParamsFromState = proNavigator.getState(state).defaultQueryParams;
        const params = defaultParamsFromState ? { ...defaultParams, ...defaultParamsFromState } : { ...defaultParams };
        return {
            state,
            params: {
                ...params,
                ...(domainParam && { key: domainParam }),
                ...(keywordParam && { keyword: keywordParam }),
                ...(industryParam && { category: industryParam }),
                ...(appNameParam && { appId: "0_" + appNameParam }),
                ...(searchSourceParam && { searchEngine: searchSourceParam }),
                ...(campaignIdParam && { campaignId: campaignIdParam }),
            },
        };
    };

    switch (actionParam) {
        case EStateNameFromLite.TECHNOLOGIES_SEEALL:
            return goToWebsiteState("digitalsuite_websites_technologiesOverview");
        case EStateNameFromLite.WEBSITE_PERFORMANCE:
            return goToWebsiteState("digitalsuite_website_websiteperformance");
        case EStateNameFromLite.TRAFFIC_ENGAGEMENT:
            return goToWebsiteState("digitalsuite_website_trafficandengagement");
        case EStateNameFromLite.SEARCH_ADS:
            return goToWebsiteState("paidsearch_website_ads");
        case EStateNameFromLite.DISPLAY_CREATIVES:
            return goToWebsiteState("digitalsuite_website_display_overview");
        case EStateNameFromLite.PAID_WEBSITE_ANALYSIS:
            return domainParam
                ? goToWebsiteState("digitalsuite_website_search_overview")
                : goToWebsiteState("paidsearch_websiteanalysis_home");
        case EStateNameFromLite.COMPETITORS_PAID_KEYWORDS:
            return goToWebsiteState("paidsearch_website_search_paid_competitors");
        case EStateNameFromLite.ORGANIC_WEBSITE_ANALYSIS:
            return domainParam
                ? goToWebsiteState("organicsearch_website_organic_search_overview")
                : goToWebsiteState("organicsearch_websiteanalysis_home");
        case EStateNameFromLite.COMPETITORS_ORGANIC_KEYWORDS:
            return goToWebsiteState("organicsearch_websites-competitorsKeywords");
        case EStateNameFromLite.SOCIAL_OVERVIEW:
            return goToWebsiteState("digitalsuite_website_social_overview");
        case EStateNameFromLite.COMPETITIVE_LANDSCAPE:
            return goToWebsiteState("digitalsuite_website_competitivelandscape");
        case EStateNameFromLite.AUDIENCE_GEOGRAPHY:
            return goToWebsiteState("digitalsuite_website_geography");
        case EStateNameFromLite.KEYWORDS_SEEALL:
            return goToWebsiteState("organicsearch_websites_v2_traffic_keywords");
        case EStateNameFromLite.FIND_SEARCH_TEXT_ADS:
            return goToWebsiteState("paidsearch_keywordAnalysis_serp_ads");
        case EStateNameFromLite.KEYWORD_GENERATOR:
        case EStateNameFromLite.KEYWORD_GENERATOR_TABLE_HOOK:
            return goToWebsiteState("digitalsuite_findkeywords_keywordGeneratorTool");
        case EStateNameFromLite.APP_ANALYSIS:
            return appNameParam
                ? goToWebsiteState("digitalsuite_app_appperformance")
                : goToWebsiteState("digitalsuite_appanalysis_home");
        case EStateNameFromLite.WEB_MARKET_ANALYSIS:
            return industryParam
                ? goToWebsiteState("digitalsuite_marketresearch_webmarketanalysis_overview")
                : goToWebsiteState("digitalsuite_webmarketanalysis_home");
        case EStateNameFromLite.KEYWORD_ANALYSIS:
            return keywordParam
                ? goToWebsiteState("organicsearch_keywordAnalysis_overview_2")
                : goToWebsiteState("organicsearch_keywordanalysis_home");
        case EStateNameFromLite.KEYWORDS_BANNER:
            return goToWebsiteState("digitalsuite_findkeywords_keywordGeneratorTool");
        case EStateNameFromLite.SEO_OVERVIEW:
            return goToWebsiteState("organicsearch_seo_overview");
        case EStateNameFromLite.AI_BRAND_VISIBILITY:
            return goToWebsiteState("digitalsuite_llm_solutions_brand_report");
        case EParamsName.LITE_REDIRECT:
            return redirectForLoggedInUsers(urlParamsCopy, defaultParams, isSI);
    }
    return null;
};

const redirectForLoggedInUsers = (
    urlParams: URLSearchParams,
    defaultParams: any,
    isSI: boolean,
): IRedirectStateLiteToPro | null => {
    const domainParam = urlParams?.get(EParamsName.DOMAIN);
    const competitorParam = urlParams?.get(EParamsName.COMPETITOR);
    const appParam = urlParams?.get(EParamsName.APP_NAME);
    const country = urlParams?.get(EParamsName.COUNTRY);
    const device = urlParams?.get(EParamsName.DEVICE);
    const store = urlParams?.get(EParamsName.STORE);
    const sourcePage = urlParams?.get(EParamsName.SOURCE_PAGE);
    let category: string | null | undefined = urlParams?.get(EParamsName.CATEGORY);
    let mode: string | null | undefined = urlParams?.get(EParamsName.MODE);

    if (category === "Overall") {
        category = "All";
    }

    mode = mode?.replace("-", "%20");

    if (sourcePage === null) {
        return null;
    }

    switch (sourcePage) {
        case EStateNameFromLite.WEBSITE_ANALYSIS: {
            const state = isSI ? WEBSITE_ANALYSIS_PERFORMANCE : "digitalsuite_website_websiteperformance";
            const defaultParamsFromState = proNavigator.getState(state).defaultQueryParams;
            const key = competitorParam === null ? domainParam : domainParam + "," + competitorParam;
            return {
                state,
                params: {
                    ...defaultParams,
                    ...defaultParamsFromState,
                    ...(key && { key }),
                },
            };
        }
        case EStateNameFromLite.APP_ANALYSIS: {
            const state = "digitalsuite_app_appperformance";
            const defaultParamsFromState = proNavigator.getState(state).defaultQueryParams;
            let appId = "";
            if (store === "Google") {
                appId = "0_" + appParam;
            } else if (store === "Apple") {
                appId = "1_" + appParam;
            }
            return {
                state,
                params: {
                    ...defaultParams,
                    ...defaultParamsFromState,
                    ...(appId && { appId }),
                },
            };
        }
        case EStateNameFromLite.TOP_WEBSITES: {
            const state = "digitalsuite_marketresearch_webmarketanalysis_mapping";
            const defaultParamsFromState = proNavigator.getState(state).defaultQueryParams;
            category = category?.replace("%2F", "~");
            return {
                state,
                params: {
                    ...defaultParams,
                    ...defaultParamsFromState,
                    ...(country && { country }),
                    ...(category && { category }),
                },
            };
        }
        case EStateNameFromLite.TOP_APPS: {
            const state = "digitalsuite_marketresearch_appmarketanalysis_top";
            const defaultParamsFromState = proNavigator.getState(state).defaultQueryParams;
            category = category?.replace("%2F", "%20>%20");
            const duration = "7d";
            return {
                state,
                params: {
                    duration,
                    ...defaultParams,
                    ...defaultParamsFromState,
                    ...(category && { category }),
                    ...(mode && { mode }),
                    ...(device && { device }),
                    ...(country && { country }),
                    ...(store && { store }),
                },
            };
        }
        case EStateNameFromLite.TOP_APPS_TRENDS: {
            const state = "digitalsuite_marketresearch_appmarketanalysis_trending";
            const defaultParamsFromState = proNavigator.getState(state).defaultQueryParams;
            category = category?.replace("%2F", "%20>%20");
            return {
                state,
                params: {
                    ...defaultParams,
                    ...defaultParamsFromState,
                    ...(category && { category }),
                    ...(mode && { mode }),
                    ...(device && { device }),
                    ...(store && { store }),
                },
            };
        }
        case EStateNameFromLite.AI_TRAFFIC: {
            const state = ROUTES.overview;
            const defaultParamsFromState = proNavigator.getState(state).defaultQueryParams;
            return {
                state,
                params: {
                    ...defaultParams,
                    ...defaultParamsFromState,
                    ...(domainParam && { key: domainParam }),
                },
            };
        }
    }
    return null;
};
